import {
  AfterViewInit,
  Component,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import {
  AccountService,
  AlertService,
  CampaignProductService,
  CampaignService,
  CampaignTrackingService,
  CRMCampaignService,
  FunnelStepService,
  GuiService,
  MerchantService,
  RefundReportService,
  ReportsConfigService,
  RetentionReportService,
  SideNavService,
  StorageService,
  UserService,
  CancelReasonCategoryService,
  FunnelService,
} from '../_services';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { ReportsAbstractV3Component } from './reports-abstract-v3.component';
import { MultiOptions } from '../custom-multi-select/multi-select';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  newOrderRetentionFields,
  newOrderRetentionFilters,
  offerGroupHeaders,
} from './utils/constants';
import { reportsFieldDataV3 } from './utils/reportsFieldData';
import { cloneDeep } from 'lodash';
import { ReportDimensions, ReportTypes, ReportVersion } from './models/reports';
import { Observable } from 'rxjs';
import { expandCollapse } from '../_shared/animations/app-animations';

@Component({
  moduleId: module.id.toString(),
  selector: 'order-retention-report-v3',
  templateUrl: './templates/reports-v3.component.html',
  styleUrls: ['./styles/reports-v2.component.scss', './styles/reports-v3.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [expandCollapse],
})
export class OrderRetentionReportV3Component extends ReportsAbstractV3Component implements OnInit, AfterViewInit {
  fields: MultiOptions = cloneDeep(newOrderRetentionFields);
  filters: MultiOptions = cloneDeep(newOrderRetentionFilters);
  reportType = ReportTypes.ORDER_RETENTION;
  offerGroupHeaders = offerGroupHeaders.map((h) => {
    if (h.key === ReportDimensions.AvgMrrSaved) {
      return { ...h, label: 'Revenue Saved' };
    }
    return h;
  });
  pctFieldMap: { [key: string]: string } = {
    hold_cancel: 'hold_cancel_pct',
    hold_save: 'hold_save_pct',
  };

  overviewFieldMap: { [summaryField: string]: string } = {
    cancel_hold_request: 'cancel_requests',
    hold_cancel: 'cancelled',
    cancel_hold_request_abandoned: 'abandoned',
    hold_save: 'saved',
    save_hold_rate: 'retention_rate',
  };

  predefinedFields = ['exclude_zeroes', 'exclude_tests'];
  commonReportsFields = [
    'cancel_hold_request',
    'item_price',
    'hold_cancel',
    'hold_save',
    'cancel_hold_request_abandoned',
    'save_hold_rate',
    'item_refund_amount',
  ];
  summaryFields = [
    'cancel_hold_request',
    'hold_cancel',
    'cancel_hold_request_abandoned',
    'hold_save',
    'save_hold_rate',
  ];
  selectedDimensions = [
    {
      model: 'cancel_reason',
      label: 'Cancel Reason',
      selected: true,
      disabled: false,
    },
  ];
  fieldsToExcludeFromReport: string[] = ['step_name', 'step_slug'];
  fieldsNotHavingEmptyValue = [
    ReportDimensions.CancelReason,
    ReportDimensions.LastHoldInput,
    ReportDimensions.LastHoldStep,
  ];
  switchFilterKeyMapping = [
    { from: ReportDimensions.ProductPath, to: ReportDimensions.HoldPath },
    { from: ReportDimensions.InputName, to: ReportDimensions.LastHoldInput },
  ];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    public reportsService: RetentionReportService,
    protected modalService: NgxSmartModalService,
    protected campaignService: CampaignService,
    protected reportsConfigService: ReportsConfigService,
    protected trackingSourceService: CampaignTrackingService,
    protected userService: UserService,
    protected accountService: AccountService,
    protected campaignProductService: CampaignProductService,
    protected crmCampaignService: CRMCampaignService,
    protected storageService: StorageService,
    protected stepService: FunnelStepService,
    protected refundReportService: RefundReportService,
    protected merchantService: MerchantService,
    protected cancelReasonCategoryService: CancelReasonCategoryService,
    protected funnelService: FunnelService,
    protected guiService: GuiService,
    protected sideNavService: SideNavService
  ) {
    super(
      router,
      location,
      route,
      alertService,
      reportsService,
      campaignService,
      reportsConfigService,
      trackingSourceService,
      userService,
      accountService,
      campaignProductService,
      crmCampaignService,
      storageService,
      stepService,
      merchantService,
      modalService,
      cancelReasonCategoryService,
      funnelService,
      guiService
    );

    this.fieldData = reportsFieldDataV3;
    this.showExcludeAbandonsFilter = true;
    this.bannerTitle = 'Order Retention Report';
    this.bannerSubtitle =
      "This report shows order-level retention outcomes, including items saved, items canceled, abandonment, refunds, and overall retention performance. Use dimensions and filters to dive deeper and understand what's happening across different order scenarios, products, and customer stages.";
  }

  ngOnInit() {
    super.ngOnInit();
    this.sideNavService.setSideNavExpanded(false);
  }

  ngAfterViewInit() {
    if (this.panel) {
      this.panel.expanded = true;
    }
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.selectedDimensions, event.previousIndex, event.currentIndex);
  }

  getQueryForSummary() {
    const query = super.getQueryForSummary();
    query['report_type'] = this.reportType;
    query['report_version'] = ReportVersion.Clickhouse;
    this.switchFilterKeys(query);
    this.applyDashboardNavFilters(query);
    return query;
  }

  getQueryForReport() {
    const query = super.getQueryForReport();
    query['report_type'] = this.reportType;
    query['report_version'] = ReportVersion.Clickhouse;
    query['show_percentage'] = this.showPercentage;
    this.switchFilterKeys(query);
    this.applyDashboardNavFilters(query);
    this.expandMergedDimensionFields(query);
    return query;
  }

  getSummaryApiCalls(): Observable<any> {
    return this.reportsService.getSummary(this.getQueryForSummary());
  }
}
